import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { fetchCurrentUserProfile, UserProfile } from '../services/api';
import { useAuth } from '../hooks/useAuth';

interface UserProfileContextType {
  userProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  refetchProfile: () => Promise<void>;
  updateProfileData: (data: UserProfile) => void;
  
  // Subscription helpers
  isTrialActive: boolean;
  hasActiveSubscription: boolean;
  creditBalance: number;
  activitiesRemaining: number;
  subscriptionStatus: string | null;
  canCreateActivity: boolean;
  /** Phase 3 profile boost: API allows only Premium-tier plans (403 for Basic/starter). */
  canUseProfileBoost: boolean;
}

const UserProfileContext = createContext<UserProfileContextType | undefined>(undefined);

interface UserProfileProviderProps {
  children: ReactNode;
}

export const UserProfileProvider: React.FC<UserProfileProviderProps> = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadProfile = async () => {
    if (!isAuthenticated) {
      setUserProfile(null);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await fetchCurrentUserProfile();
      setUserProfile(data);
    } catch (err) {
      console.error('Error loading user profile:', err);
      setError('Failed to load profile');
      setUserProfile(null);
    } finally {
      setLoading(false);
    }
  };

  const refetchProfile = async () => {
    await loadProfile();
  };

  const updateProfileData = (data: UserProfile) => {
    setUserProfile(data);
  };

  useEffect(() => {
    // Only load profile if auth is not loading and user is authenticated
    if (!authLoading && isAuthenticated && !userProfile) {
      loadProfile();
    } else if (!authLoading && !isAuthenticated) {
      setUserProfile(null);
    }
  }, [isAuthenticated, authLoading, userProfile]);

  // Calculate subscription helper values from new unified structure
  const subscription = userProfile?.subscription;
  const isTeamMember = userProfile?.is_team_member === true || subscription?.type === 'team_member';
  const isGlobalFree = subscription?.type === 'global_free_access' && subscription?.status === 'active';
  const isTrialActive = subscription?.trial?.is_active || false;
  const hasActiveSubscription = (subscription?.type === 'subscription' && subscription?.status === 'active') || isTeamMember || isGlobalFree;
  const creditBalance = typeof subscription?.credit_balance === 'number' ? subscription.credit_balance : 0;
  const activitiesRemaining = typeof subscription?.activities_remaining === 'number' ? subscription.activities_remaining : 0;
  const subscriptionStatus = subscription?.type || null;
  const canCreateActivity = isTeamMember || isGlobalFree || subscription?.can_create_activity || activitiesRemaining === -1;

  const planSlug = subscription?.plan?.name?.toLowerCase() ?? '';
  const isPremiumTierPlan = planSlug === 'growth' || planSlug === 'annual';
  const canUseProfileBoost =
    isTeamMember ||
    isGlobalFree ||
    (subscription?.status === 'active' &&
      isPremiumTierPlan &&
      (subscription?.type === 'subscription' || (subscription?.type === 'trial' && isTrialActive)));

  console.log('✅ UserProfileContext - New Structure:', {
    subscription_type: subscription?.type,
    subscription_status: subscription?.status,
    isTeamMember,
    isGlobalFree,
    isTrialActive,
    hasActiveSubscription,
    creditBalance,
    activitiesRemaining,
    canCreateActivity,
    trial_days_remaining: subscription?.trial?.days_remaining,
    plan_name: subscription?.plan?.display_name || 'No plan',
    expires_at: subscription?.expires_at || null,
    canUseProfileBoost,
  });

  const value: UserProfileContextType = {
    userProfile,
    loading,
    error,
    refetchProfile,
    updateProfileData,
    
    // Subscription helpers
    isTrialActive,
    hasActiveSubscription,
    creditBalance,
    activitiesRemaining,
    subscriptionStatus,
    canCreateActivity,
    canUseProfileBoost,
  };

  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
};

export const useUserProfile = () => {
  const context = useContext(UserProfileContext);
  if (context === undefined) {
    throw new Error('useUserProfile must be used within a UserProfileProvider');
  }
  return context;
}; 